import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from "recompose";
import { connect } from "react-redux";
import { withStyles, Paper, Button } from "@material-ui/core";
import "leaflet-draw/dist/leaflet.draw.css";
import MapComponent, {defaultCenter} from '../../MapComponents/MapComponent';
import {Marker} from "react-leaflet";
import SavedLocationsTable from "./SavedLocations/SavedLocationsTable";
import { bindActionCreators } from 'redux';
import ActionCreator from './../../../store/actions/index';
import AddPoi from './AddPoi';
import ImportOrExportFileWithTemplate from './../../Common/ImportOrExportFileWithTemplate';
import PoiTemplate from "../../../templates/PoiTemplate.csv";
import t from "../../../languages/pl";
import SearchField from "../../Common/InputFields/SearchField";
import { isNullOrEmpty, notArrayOrEmpty, convertLngToLon } from './../../../utils/helpers';
import ConfirmationDialog from "../../Common/ConfirmationDialog";

const useStyles = theme => ({
    paper: {
        margin: theme.spacing(1),
        padding: theme.spacing(1),
        display: "flex",
        justifyContent: "space-between"
    },
    form: {
        width: "50%",
        padding: theme.spacing(1)
    },
    mapContainer: {
        width: "50%"
    },
    addNewLabel: {
        display: "flex",
        justifyContent: 'space-between',
        alignItems: "center",
        paddingLeft: theme.spacing(2)
    },
    collapseContainer: {
        border: "1px solid",
        borderRadius: 10,
        borderColor: "lightgrey",
        position: "relative"
    },
    collapseContent: {
        display: "flex",
    },
    buttonsBar: {
        position: "absolute",
        top: 60,
        right: 0,
        display: "flex",
        flexDirection: "column"
    },
    tableTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: theme.spacing(2),
        margin: theme.spacing(1),
        marginBottom: 0
    },
    buttons: {
        display: "flex"
    }
});

const csvHeaders = [
    {label: "Adres", key: "adres"},
    {label: "Typ", key: "typ"},
    {label: "Nazwa", key: "nazwa"}
];

const PoiView = props => {
    const { classes, savedLocations, poiCoords, importPoi } = props;
    const mapRef = useRef(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [searchedLocation, setSearchedLocation] = useState('');
    const [dataToExport, setDataToExport] = useState(null);
    const [ poiId, setPoiId] = useState(null);
    const [addNewOpen, setAddNewOpen] = useState(false);
    const [poiAfterSearch, setPoiAfterSearch] = useState(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

    useEffect(() => {
        if(mapRef.current !== null)
            mapRef.current.leafletElement.setView(defaultCenter, 12);
    }, []);

    useEffect(() => {
        if(mapRef.current !== null){
            mapRef.current.leafletElement.flyTo(poiCoords, 12);
        }
    }, [poiCoords]);

    useEffect(() => {
        if(notArrayOrEmpty(savedLocations))
            return;
        let data = savedLocations.map(({adres, nazwa, typ}) => ({adres, nazwa, typ}));
        setDataToExport(data);
    }, [savedLocations]);

    useEffect(() => {
        selectedLocation && props.changePoiCoords(selectedLocation.punkt_gps);
    }, [selectedLocation]);

    const handleFileImport = data => {
        let importedPoiLocations = data.map(row => {
            let converted;
            csvHeaders.forEach(header => {
                if(header.label in row)
                    converted = {...converted, [header.key]: row[header.label]};
            });
            return converted;
        });
        importPoi(importedPoiLocations);
    };

    const handleRowExpand = id => {
        if (poiId !== id) {
            setPoiId(id);
            setAddNewOpen(false);
        }
        else {
            setPoiId(null);
        }
        setPoiAfterSearch(null);
    };

    const handleExpandNewPoi = () => {
        setAddNewOpen(!addNewOpen);
        setPoiId(null);
        setSelectedLocation(null);
        setPoiAfterSearch(null);
    };

    const handleSearch = (newData) => {
        props.getAddressForPoi(newData.adres);
        setPoiAfterSearch(newData);
    };

    const handlePoiUpdate =(data) => {
        props.putPoi(data.poi_id, data, props.getPoiCoords());
        setPoiAfterSearch(null);
    };

    const handleDeletePoi = () => {
        props.deleteAllPoi();
    };

    return (
        <Paper
            square
            className={classes.paper}
        >
            <div className={classes.form}>
                <AddPoi
                    onSearch={(address) => props.getAddressForPoi(address)}
                    onAdd={(data) => props.addNewPoi(data, props.getPoiCoords())}
                    isNewOpen={addNewOpen}
                    onExpandAddPoi={handleExpandNewPoi}
                />
                <div className={classes.tableTitle}>
                    <SearchField onChange={(value) => setSearchedLocation(value)} />
                    <div className={classes.buttons}>
                        <ImportOrExportFileWithTemplate
                            tooltipText={t.importCsvDescriptionPOILocations}
                            template={PoiTemplate}
                            exportFilename={t.poiExportFilename}
                            exportCsvHeaders={csvHeaders}
                            exportData={dataToExport}
                            onFileLoaded={handleFileImport}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setShowConfirmationDialog(true)}
                        >
                            {t.deleteAllPoi}
                        </Button>
                    </div>
                </div>
                <SavedLocationsTable
                    selected={selectedLocation}
                    locations={isNullOrEmpty(searchedLocation) ? savedLocations : savedLocations.filter(location => location.nazwa.toLowerCase().includes(searchedLocation.toLowerCase()))}
                    onNewSelected={(data) => setSelectedLocation(data)}
                    onPoiDelete={(id) => props.deletePoi(id)}
                    onExpand={handleRowExpand}
                    expandedId={poiId}
                    onSearch={handleSearch}
                    onPoiUpdate={handlePoiUpdate}
                    dataAfterSearch={poiAfterSearch}
                />
            </div>
            <div className={classes.mapContainer}>
                <MapComponent
                    ref={mapRef}
                    style={{height: "78vh"}}
                >
                    <Marker
                        position={poiCoords}
                        draggable={poiId ? false : true}
                        ondragend={(e) => props.changePoiCoords(convertLngToLon(e.target._latlng))}
                    />
                </MapComponent>
            </div>
            <ConfirmationDialog
                open={showConfirmationDialog}
                onClose={() => setShowConfirmationDialog(false)}
                onSubmit={handleDeletePoi}
                action={t.deleteAllPoiInformation}
                warningText={t.irreversibleActionInformation}
            />
        </Paper>
    );
};

const mapStateToProps = (state) => ({
    savedLocations: state.settingsReducer.poiLocations,
    poiCoords: state.settingsReducer.poiCoords
});

const mapDispatchToProps = dispatch => bindActionCreators(ActionCreator, dispatch);


PoiView.propTypes = {
    classes: PropTypes.object,
    //redux
    savedLocations: PropTypes.array,
    poiCoords: PropTypes.object,
    getAddressForPoi: PropTypes.func,
    changePoiCoords: PropTypes.func,
    addNewPoi: PropTypes.func,
    deletePoi: PropTypes.func,
    putPoi: PropTypes.func,
    deleteAllPoi: PropTypes.func,
    importPoi: PropTypes.func,
    getPoiCoords: PropTypes.func,
};

export default compose(
    withStyles(useStyles),
    connect(
        mapStateToProps,
        mapDispatchToProps
    ))(PoiView);